import { Box, Modal, Text, Textarea } from '@tocktix/pantry';
import { Form, Formik, FormikErrors } from 'formik';
import { FunctionComponent, useState } from 'react';
import { userClient } from 'tools/bff/client';

interface DeactivateUserFormValues {
  reason: string;
}

interface DeactivateUserModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const validate = (values: DeactivateUserFormValues) => {
  const errors: FormikErrors<DeactivateUserFormValues> = {};
  if (!values.reason.trim()) {
    errors.reason = 'Reason is required';
  }
  return errors;
};

const DeactivateUserModal: FunctionComponent<DeactivateUserModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = (values: DeactivateUserFormValues) => {
    return userClient
      .deactivateUsers({ uuids: [uuid], reason: values.reason.trim() })
      .then(onSubmit)
      .then(handleClose)
      .catch((reason) => setError('Error deactivating user: ' + reason));
  };

  return (
    <Formik<DeactivateUserFormValues>
      initialValues={{ reason: '' }}
      validate={validate}
      onSubmit={handleSubmission}
    >
      {({ dirty, errors, handleSubmit, isSubmitting, isValid, setFieldValue, touched, values }) => (
        <Form id="deactivate-user-form" data-testid="deactivate-user-form">
          <Modal
            id="deactivate-user-modal"
            open={open}
            onClose={handleClose}
            size="md"
            data-testid="deactivate-user-modal"
          >
            <Modal.Header title="Deactivate account" onClose={handleClose} border />
            <Modal.Body>
              <Box display="flex" flexDirection="column" gap={200}>
                <Text variant="body" color="fgSubtle">
                  This will deactivate the account and prevent the user from using it until it is
                  reactivated.
                </Text>
                <Textarea
                  id="deactivate-user-reason"
                  label="Reason"
                  value={values.reason}
                  onChange={(event) => setFieldValue('reason', event.target.value)}
                  required
                  fullWidth
                  data-testid="deactivate-user-reason-input"
                  validationStatus={touched.reason && errors.reason ? 'error' : undefined}
                  validationMessage={touched.reason ? errors.reason : undefined}
                />
                {error && (
                  <Text variant="body" color="fgDangerBase">
                    {error}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Deactivating...' : 'Deactivate',
                  danger: true,
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: handleClose,
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default DeactivateUserModal;
